import classNames from 'classnames';
import React from 'react';
import { CheckedBlueIcon } from '../icons';

export interface ListWithStatusItem {
  title?: string;
  description?: string;
  blockInfo?: React.ReactNode;
  onClick?: () => void;
  active?: boolean;
  disabled?: boolean;
}

export interface ListWithStatusProps {
  items: ListWithStatusItem[];
  className?: string;
  classNameTitle?: string;
  classNameDescription?: string;
  addBorderLeft?: boolean;
  addBorderBottom?: boolean;
  isError?: boolean;
  activeTitleLineThrough?: boolean;
}

export const ListWithStatus = ({
  items,
  className,
  classNameTitle,
  classNameDescription,
  addBorderLeft = true,
  addBorderBottom = false,
  isError = false,
  activeTitleLineThrough = true,
}: ListWithStatusProps) => {
  if (!items?.length) {
    return (
      <EmptyStateList
        isError={isError}
        addBorderBottom={addBorderBottom}
        addBorderLeft={addBorderLeft}
        className={className}
      />
    );
  }

  return (
    <div className={classNames('flex', className)}>
      <div className="flex flex-col gap-4 w-full">
        {items?.map((item, i, all) => (
          <div key={i + '_' + item.title} className="flex gap-3">
            {addBorderLeft === true && (
              <span className="w-5 h-[calc(100%+12px)] flex flex-col justify-center items-center gap-1">
                <span
                  className={classNames(
                    'w-5 max-w-5 min-w-5 h-5 max-h-5 min-h-5 flex-1 rounded-full',
                    {
                      'border border-[#0369A1]': !item.disabled && item.active !== true,
                      'border border-[#E3E3E3]': item.disabled === true,
                    },
                  )}
                >
                  {!item.disabled && item.active === true && (
                    <img src={CheckedBlueIcon} className="w-5 min-w-5 h-5 min-h-5" alt="checked" />
                  )}
                </span>
                <span
                  className={classNames('h-full flex-1', {
                    'w-[1px] min-w-[1px] border-l': i < all.length - 1,
                    // 'border-l-[#70FF00]': i < all.length - 1 && item.active === true,
                    'border-l-gray-200': i < all.length - 1, // && !item.active,
                    'w-0': i === all.length - 1,
                  })}
                ></span>
              </span>
            )}
            <span
              className={classNames('flex flex-col w-full', {
                'hover:cursor-pointer hover:bg-gray-50': !!item.onClick,
                'border-b pb-3': addBorderBottom === true && i < all.length - 1,
              })}
              onClick={item.onClick}
            >
              {item.title && (
                <span
                  className={classNames(
                    {
                      'text-sm text-[#111827]': !classNameTitle?.includes('text-'),
                      'leading-[22px]': !classNameTitle?.includes('leading-'),
                      'line-through !text-gray-400': item.active && activeTitleLineThrough === true,
                    },
                    classNameTitle,
                  )}
                >
                  {item.title}
                </span>
              )}
              {item.description && (
                <span
                  className={classNames(
                    {
                      'text-xs text-gray-600': !classNameDescription?.includes('text-'),
                      'leading-5': !classNameDescription?.includes('leading-'),
                    },
                    classNameDescription,
                  )}
                >
                  {item.description}
                </span>
              )}
              {item.blockInfo && <>{item.blockInfo}</>}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

interface EmptyStateListProps {
  isError?: boolean;
  addBorderLeft?: boolean;
  addBorderBottom?: boolean;
  className?: string;
}

const EmptyStateList = ({
  isError = false,
  addBorderLeft = true,
  addBorderBottom = false,
  className,
}: EmptyStateListProps) => {
  return (
    <div className={classNames('flex w-full', className)}>
      <div className="relative w-full h-full pt-1 flex flex-col gap-4">
        {[1, 2, 3].map((item, i, all) => (
          <div key={item} className="flex gap-3">
            {addBorderLeft === true && (
              <span className="w-5 h-[calc(100%+12px)] flex flex-col justify-center items-center gap-1">
                <span className="w-5 max-w-[20px] min-w-[20px] h-5 max-h-[20px] min-h-[20px] flex-1 rounded-full border border-gray-200"></span>
                <span
                  className={classNames('h-full flex-1', {
                    'w-[1px] min-w-[1px] border-x border-x-gray-200': i < all.length - 1,
                    'w-0': i === all.length - 1,
                  })}
                ></span>
              </span>
            )}
            <span
              className={classNames('flex flex-col w-full pt-1.5', {
                'border-b pb-3': addBorderBottom === true && i < all.length - 1,
              })}
            >
              <span className="flex flex-col gap-3 w-full h-9">
                <span className="flex w-[88px] gap-4">
                  <span className="w-[45px] border-4 rounded-lg"></span>
                  <span className="w-[55px] border-4 rounded-lg"></span>
                </span>
                <span className="w-[60px] border-4 rounded-lg"></span>
              </span>
            </span>
          </div>
        ))}
        <div
          className={classNames(
            'absolute flex items-center justify-center m-auto top-0 left-0 right-0 bottom-0',
            'text-[16px] leading-6 font-bold text-[#475569]',
          )}
        >
          {isError ? 'Error loading:(' : 'No data to display'}
        </div>
      </div>
    </div>
  );
};
